/**
 * NaLogModal — Not Available Log
 * Shows equipment and badges marked Not Available this shift.
 * Reads from localStorage.fom_na_log. Includes a Print button.
 */
import { modalOverlay, modalBox } from './modalShared'

interface NaLogEntry {
  type: string
  itemId: string
  name: string
  time: string
  note: string
}

function loadEntries(): NaLogEntry[] {
  try { return JSON.parse(localStorage.getItem('fom_na_log') ?? '[]') } catch { return [] }
}

function printLog(entries: NaLogEntry[]) {
  const rows = entries.map(e =>
    `<tr>
      <td>${e.type}</td>
      <td>${e.itemId}</td>
      <td>${e.name || '—'}</td>
      <td>${e.time || '—'}</td>
      <td>${e.note || '—'}</td>
    </tr>`
  ).join('')
  const html = `<html><head><title>NA Log</title>
  <style>
    body{font-family:Calibri,Arial,sans-serif;font-size:10pt;margin:0.5in}
    h2{margin-bottom:4px}p{margin-top:0;color:#555;font-size:9pt}
    table{border-collapse:collapse;width:100%}
    th,td{border:1px solid #ccc;padding:5px 8px;text-align:left;font-size:9pt}
    th{background:#f0f0f0;font-weight:700}
  </style></head><body>
  <h2>⚠ Not Available Log</h2>
  <p>Equipment and badges marked Not Available — ${new Date().toLocaleString('en-US',{timeZone:'America/New_York'})}</p>
  ${entries.length === 0
    ? '<p style="color:#999">No items currently marked as Not Available.</p>'
    : `<table><thead><tr><th>Type</th><th>Item</th><th>Officer</th><th>Time</th><th>Note</th></tr></thead><tbody>${rows}</tbody></table>`
  }
  </body></html>`
  const w = window.open('', '_blank')
  if (w) { w.document.write(html); w.document.close(); w.focus(); setTimeout(() => w.print(), 400) }
}

export default function NaLogModal({ onClose }: { onClose: () => void }) {
  const entries = loadEntries()

  const overlay: React.CSSProperties = {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000,
  }

  const box: React.CSSProperties = {
    ...modalBox(600),
    background: '#0f1c2e',
    borderRadius: 12,
    border: '1px solid rgba(255,255,255,0.08)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '80vh',
  }

  const th: React.CSSProperties = {
    padding: '7px 10px', fontSize: 11, fontWeight: 700,
    color: 'var(--text-muted)', textAlign: 'left',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    background: '#0f1c2e', position: 'sticky', top: 0,
    whiteSpace: 'nowrap',
  }
  const td: React.CSSProperties = {
    padding: '6px 10px', fontSize: 12, color: 'var(--text)',
    borderBottom: '1px solid rgba(255,255,255,0.04)',
    whiteSpace: 'nowrap',
  }

  return (
    <div style={overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={box}>
        {/* Header */}
        <div style={{
          padding: '14px 16px 10px',
          borderBottom: '1px solid rgba(255,255,255,0.07)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 3 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 18 }}>⚠️</span>
              <span style={{
                fontSize: 16, fontWeight: 700,
                color: entries.length > 0 ? '#ef4444' : '#f59e0b',
              }}>
                Not Available Log ({entries.length} item{entries.length !== 1 ? 's' : ''})
              </span>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button
                onClick={() => printLog(entries)}
                style={{
                  background: '#7c3aed', border: 'none', borderRadius: 8,
                  padding: '6px 16px', color: '#fff', fontWeight: 700,
                  fontSize: 13, cursor: 'pointer',
                }}
              >Print</button>
              <button
                onClick={onClose}
                style={{
                  background: 'none', border: 'none', color: 'var(--text-muted)',
                  fontSize: 18, cursor: 'pointer', lineHeight: 1, padding: '2px 4px',
                }}
              >×</button>
            </div>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            Equipment and badges marked Not Available
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {entries.length === 0 ? (
            <div style={{
              color: 'var(--text-hint)', textAlign: 'center',
              padding: '36px 0', fontSize: 13,
            }}>
              No items currently marked as Not Available.
            </div>
          ) : (
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead>
                <tr>
                  <th style={th}>Type</th>
                  <th style={th}>Item</th>
                  <th style={th}>Officer</th>
                  <th style={th}>Time</th>
                  <th style={{ ...th, width: '100%' }}>Note</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)' }}>
                    <td style={{ ...td, color: '#f87171', fontWeight: 700 }}>{e.type}</td>
                    <td style={{ ...td, color: 'var(--accent)', fontWeight: 600 }}>{e.itemId}</td>
                    <td style={td}>{e.name || '—'}</td>
                    <td style={{ ...td, color: 'var(--text-muted)', fontSize: 11 }}>{e.time || '—'}</td>
                    <td style={{ ...td, color: 'var(--text-muted)', whiteSpace: 'normal' }}>{e.note || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  )
}
